'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const PLAN = {
  id: 'pro',
  name: 'NHL Analytics Pro',
  price: 29,
  color: 'text-primary',
  border: 'border-primary/30',
  features: [
    'Projections ML illimitées',
    'Live Analytics NHL',
    'ML Engine complet',
    'Alertes avancées (6 types)',
    'API illimitée',
    'Export CSV/JSON',
    'Analytics Copilot (GPT-4)',
    'Monte Carlo avancé (50k iter.)',
    'SimulationLab complet',
    'Player Intelligence',
    'Market Intelligence',
    'Event Detection',
    'Webhooks temps réel',
    'Custom AI Models',
    'Quant Terminal',
    'Données brutes NHL',
    'SLA 99.9% garanti',
    'Support prioritaire 24h',
  ],
  limits: { projections: -1, api_calls: -1, live_analytics: true, ml_engine: true },
};

const MRR_DATA = [
  { month: 'Nov', mrr: 28450, users: 3421 },
  { month: 'Dec', mrr: 31200, users: 3687 },
  { month: 'Jan', mrr: 35890, users: 3987 },
  { month: 'Feb', mrr: 39120, users: 4211 },
  { month: 'Mar', mrr: 43450, users: 4510 },
  { month: 'Apr', mrr: 46890, users: 4720 },
  { month: 'May', mrr: 48750, users: 4847 },
];

const PLAN_DISTRIBUTION = [
  { plan: 'Pro', users: 955, color: '#00D4FF' },
];

const TOOLTIP_STYLE = { backgroundColor: '#111827', border: '1px solid #1E2D4A', borderRadius: '6px', color: '#E8EDF5', fontSize: '11px' };

export default function MonetisationClient() {
  const [activeTab, setActiveTab] = useState<'plans' | 'metrics' | 'billing'>('plans');

  const totalMRR = 48750;
  const churnRate = 2.3;
  const conversionRate = 18.7;
  const avgLTV = 287;

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Monétisation SaaS</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Plan unique $29/mois · Tout inclus · Stripe Billing · Métriques SaaS</p>
        </div>
        <div className="flex items-center gap-2 bg-card border border-border rounded-lg p-1">
          {(['plans', 'metrics', 'billing'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${activeTab === tab ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {tab === 'plans' ? 'Plan' : tab === 'metrics' ? 'Métriques SaaS' : 'Billing Portal'}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'plans' && (
        <>
          {/* Single Plan Card */}
          <div className="max-w-lg mx-auto">
            <div className={`card p-6 relative ${PLAN.border} ring-1 ring-primary/30`}>
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-primary text-white text-2xs font-bold px-4 py-1 rounded-full">ACCÈS COMPLET</span>
              </div>
              <div className="text-center mb-6">
                <div className={`text-xs font-bold tracking-widest ${PLAN.color} mb-3`}>{PLAN.name}</div>
                <div className="flex items-baseline gap-1 justify-center mb-1">
                  <span className="text-5xl font-bold text-foreground">$29</span>
                  <span className="text-xs text-muted-foreground">/mois</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Toutes les fonctionnalités incluses · Annulation à tout moment</p>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mb-6">
                {PLAN.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="CheckIcon" size={12} className="text-positive flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <button className="w-full py-2.5 rounded text-sm font-semibold transition-colors bg-primary text-white hover:bg-primary/90">
                Choisir ce Plan
              </button>
            </div>
          </div>

          {/* Coupons & Referral */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="TagIcon" size={16} className="text-primary" />
                Coupons & Essais Gratuits
              </h3>
              <div className="space-y-3">
                {[
                  { code: 'WELCOME30', discount: '30%', uses: '234/500', expires: '31 mai 2026', type: 'Nouveau compte' },
                  { code: 'ANNUAL20', discount: '20%', uses: '89/200', expires: '30 juin 2026', type: 'Plan annuel' },
                  { code: 'TRIAL14', discount: '14 jours', uses: '567/∞', expires: 'Permanent', type: 'Essai gratuit' },
                ].map((coupon) => (
                  <div key={coupon.code} className="flex items-center justify-between p-2 rounded bg-card-hover">
                    <div>
                      <span className="text-xs font-mono-data font-bold text-primary">{coupon.code}</span>
                      <span className="text-2xs text-muted-foreground ml-2">{coupon.type}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-semibold text-positive">{coupon.discount}</div>
                      <div className="text-2xs text-muted-foreground">{coupon.uses} utilisations</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="UserGroupIcon" size={16} className="text-positive" />
                Programme de Parrainage
              </h3>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-positive/5 border border-positive/20">
                  <div className="text-xs font-semibold text-positive mb-1">Votre code de parrainage</div>
                  <div className="font-mono-data text-lg text-foreground">NHL-REF-X7K2</div>
                  <div className="text-2xs text-muted-foreground mt-1">Partagez et gagnez 1 mois gratuit par filleul</div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Filleuls', value: '12' },
                    { label: 'Mois Gagnés', value: '3' },
                    { label: 'Valeur', value: '$87' },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center p-2 rounded bg-card-hover">
                      <div className="text-lg font-bold font-mono-data text-primary">{stat.value}</div>
                      <div className="text-2xs text-muted-foreground">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'metrics' && (
        <>
          {/* SaaS KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'MRR', value: `$${totalMRR.toLocaleString()}`, sub: '+4.0% vs mois dernier', icon: 'CurrencyDollarIcon', color: 'text-positive' },
              { label: 'ARR', value: `$${(totalMRR * 12).toLocaleString()}`, sub: 'Annualisé', icon: 'ChartBarIcon', color: 'text-primary' },
              { label: 'Churn Rate', value: `${churnRate}%`, sub: 'Mensuel · Objectif <2%', icon: 'ArrowTrendingDownIcon', color: 'text-warning' },
              { label: 'Conversion', value: `${conversionRate}%`, sub: 'Trial → Payant', icon: 'ArrowTrendingUpIcon', color: 'text-positive' },
              { label: 'LTV Moyen', value: `$${avgLTV}`, sub: 'Par utilisateur payant', icon: 'UserIcon', color: 'text-primary' },
              { label: 'Total Users', value: '4,847', sub: '+127 aujourd\'hui', icon: 'UsersIcon', color: 'text-foreground' },
              { label: 'Trial Starts', value: '234', sub: 'Ce mois · 67 convertis', icon: 'PlayIcon', color: 'text-primary' },
              { label: 'NPS Score', value: '72', sub: 'Excellent · +3 pts', icon: 'StarIcon', color: 'text-warning' },
            ].map((kpi) => (
              <div key={kpi.label} className="card p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name={kpi.icon as Parameters<typeof Icon>[0]['name']} size={14} className={kpi.color} />
                  <span className="text-xs text-muted-foreground">{kpi.label}</span>
                </div>
                <div className={`text-2xl font-bold font-mono-data ${kpi.color}`}>{kpi.value}</div>
                <div className="text-2xs text-muted-foreground mt-1">{kpi.sub}</div>
              </div>
            ))}
          </div>

          {/* MRR Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4">Évolution MRR (7 mois)</h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={MRR_DATA}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" />
                  <XAxis dataKey="month" tick={{ fill: '#6B7A99', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#6B7A99', fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => [`$${v.toLocaleString()}`, 'MRR']} />
                  <Line type="monotone" dataKey="mrr" stroke="#00D4FF" strokeWidth={2.5} dot={{ fill: '#00D4FF', r: 3 }} name="MRR" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="card p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4">Abonnés Actifs</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={PLAN_DISTRIBUTION}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" />
                  <XAxis dataKey="plan" tick={{ fill: '#6B7A99', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#6B7A99', fontSize: 10 }} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="users" radius={[3, 3, 0, 0]} name="Utilisateurs" fill="#00D4FF" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {activeTab === 'billing' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="CreditCardIcon" size={16} className="text-primary" />
              Stripe Billing Portal
            </h3>
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-positive/5 border border-positive/20">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="CheckCircleIcon" size={14} className="text-positive" />
                  <span className="text-xs font-semibold text-positive">Stripe Connecté</span>
                </div>
                <div className="text-2xs text-muted-foreground">Paiements sécurisés · PCI DSS compliant</div>
              </div>
              {[
                { label: 'Plan actif', value: 'NHL Analytics Pro — $29/mois' },
                { label: 'Prochain paiement', value: '1 juin 2026' },
                { label: 'Méthode', value: 'Visa •••• 4242' },
                { label: 'Statut', value: 'Actif' },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <span className="text-xs text-muted-foreground">{item.label}</span>
                  <span className="text-xs font-medium text-foreground">{item.value}</span>
                </div>
              ))}
              <button className="w-full mt-2 py-2 rounded text-xs font-semibold border border-border text-foreground hover:bg-card-hover transition-colors">
                Gérer l'abonnement →
              </button>
            </div>
          </div>

          <div className="card p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="ClockIcon" size={16} className="text-primary" />
              Transactions Récentes
            </h3>
            <div className="space-y-2">
              {[
                { user: 'j.martin@...', plan: 'Pro', amount: '$29', status: 'success', time: '2m ago' },
                { user: 'a.tremblay@...', plan: 'Pro', amount: '$29', status: 'success', time: '15m ago' },
                { user: 'p.gagnon@...', plan: 'Pro', amount: '$29', status: 'success', time: '1h ago' },
                { user: 'l.bouchard@...', plan: 'Pro', amount: '$29', status: 'success', time: '2h ago' },
                { user: 'm.roy@...', plan: 'Pro', amount: '$29', status: 'failed', time: '3h ago' },
                { user: 'c.lavoie@...', plan: 'Pro', amount: '$29', status: 'success', time: '4h ago' },
              ].map((tx, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded hover:bg-card-hover transition-colors">
                  <div>
                    <div className="text-xs text-foreground">{tx.user}</div>
                    <div className="text-2xs text-muted-foreground">{tx.plan} · {tx.time}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-mono-data font-semibold text-foreground">{tx.amount}</div>
                    <div className={`text-2xs font-medium ${tx.status === 'success' ? 'text-positive' : 'text-negative'}`}>
                      {tx.status === 'success' ? '✓ Succès' : '✗ Échoué'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
